/**
 * Сборка описания раскладки из компактного табличного вида.
 *
 * Ряд задаётся строкой базовых символов и строкой символов с Shift, а также
 * последовательностью пальцев. Так данные раскладки читаются глазами и
 * проверяются тестом на согласованность длин.
 */

import type { Finger, KeyboardForm } from '@type2x/shared-contracts';

import type { KeyDefinition, KeyRow } from './types.js';

/** Компактное описание одного ряда. */
export interface RowSpec {
  readonly row: KeyRow;
  /** Символы без Shift, по одному на клавишу. */
  readonly base: readonly string[];
  /** Символы с Shift. Пустая строка — клавиша не даёт второго символа. */
  readonly shifted: readonly string[];
  /** Пальцы, по одному на клавишу. */
  readonly fingers: readonly Finger[];
  /** Формы клавиатуры, на которых ряд присутствует целиком. */
  readonly forms?: readonly KeyboardForm[];
}

const ALL_FORMS: readonly KeyboardForm[] = ['ansi', 'iso'];

/**
 * Разворачивает ряды в плоский список клавиш.
 *
 * @throws {Error} если длины base, shifted и fingers не совпадают: молчаливое
 *   выравнивание превратило бы опечатку в неверную подсказку пальца.
 */
export function buildKeys(rows: readonly RowSpec[]): KeyDefinition[] {
  const keys: KeyDefinition[] = [];

  for (const spec of rows) {
    const { row, base, shifted, fingers } = spec;
    if (base.length !== shifted.length || base.length !== fingers.length) {
      throw new Error(
        `Ряд ${row}: несовпадение длин (base=${base.length}, shifted=${shifted.length}, fingers=${fingers.length})`,
      );
    }

    for (let column = 0; column < base.length; column += 1) {
      keys.push({
        base: base[column] ?? '',
        shifted: shifted[column] ?? '',
        finger: fingers[column] as Finger,
        row,
        column,
        forms: spec.forms ?? ALL_FORMS,
      });
    }
  }

  return keys;
}

/** Раскладывает строку на массив отдельных символов. */
export function chars(source: string): string[] {
  return [...source];
}

/** Повторяет палец нужное число раз — сокращает описание рядов. */
export function repeat(finger: Finger, times: number): Finger[] {
  return Array.from({ length: times }, () => finger);
}
